import { useCallback, useEffect, useRef, useState } from 'react';
import { CircularProgress, Typography, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import {
  useApi,
  fetchApiRef,
  discoveryApiRef,
} from '@backstage/core-plugin-api';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

/**
 * Scaffolder field extension that loads existing infrastructure config
 * from the backend API when githubRepository and applicationName are set.
 *
 * Renders in step 1 alongside the repository/app name fields.
 * Stores the loaded config as a JSON string via onChange.
 */
export function InfrastructureConfigLoader(
  props: FieldExtensionComponentProps<string>,
) {
  const { onChange, formContext } = props;
  const classes = useStyles();
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const [status, setStatus] = useState<
    'idle' | 'loading' | 'success' | 'not-found' | 'error'
  >('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Access sibling field values from the current step's formData
  const formData = formContext?.formData as Record<string, unknown> | undefined;
  const githubRepository = formData?.githubRepository as string | undefined;
  const applicationName = formData?.applicationName as string | undefined;

  // Track what we last fetched to avoid duplicate requests
  const lastFetchedRef = useRef('');

  const doFetch = useCallback(
    async (repo: string, app: string) => {
      const key = `${repo}/${app}`;
      if (lastFetchedRef.current === key) return;
      lastFetchedRef.current = key;

      setStatus('loading');
      setErrorMessage('');

      try {
        const baseUrl = await discoveryApi.getBaseUrl('rubicon');
        const url = `${baseUrl}/infrastructure-config?repo=${encodeURIComponent(
          repo,
        )}&app=${encodeURIComponent(app)}`;

        const response = await fetchApi.fetch(url);

        if (!response.ok) {
          const body = (await response.json().catch(() => ({}))) as Record<
            string,
            unknown
          >;
          throw new Error(
            (body.error as string) ??
              `HTTP ${response.status}: ${response.statusText}`,
          );
        }

        const data = (await response.json()) as Record<string, unknown>;

        if (data.configFound === false) {
          setStatus('not-found');
          onChange(undefined);
          return;
        }

        setStatus('success');
        onChange(JSON.stringify(data));
      } catch (e) {
        setStatus('error');
        setErrorMessage(e instanceof Error ? e.message : String(e));
        onChange(undefined);
      }
    },
    [fetchApi, discoveryApi, onChange],
  );

  useEffect(() => {
    if (!githubRepository || !applicationName) {
      setStatus('idle');
      return;
    }

    // Validate before fetching
    if (!/^[a-zA-Z0-9][a-zA-Z0-9._-]*$/.test(githubRepository)) return;
    if (!/^[a-z][a-z0-9-]*$/.test(applicationName)) return;

    doFetch(githubRepository, applicationName);
  }, [githubRepository, applicationName, doFetch]);

  return (
    <Box className={classes.root}>
      {status === 'loading' && (
        <Box className={classes.loading}>
          <CircularProgress size={20} />
          <Typography variant="body2">
            Loading existing configuration...
          </Typography>
        </Box>
      )}
      {status === 'success' && (
        <Alert severity="success">
          Existing configuration loaded. Fields in the following steps will be
          pre-populated with current values.
        </Alert>
      )}
      {status === 'not-found' && (
        <Alert severity="info">
          No existing configuration found. This appears to be a new application
          — default values will be used.
        </Alert>
      )}
      {status === 'error' && (
        <Alert severity="warning">
          Could not load existing configuration: {errorMessage}. You can still
          fill in values manually.
        </Alert>
      )}
    </Box>
  );
}
